import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Connection, PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import Header from './Header';
import Footer from './Footer';
import qrImage from '../images/aPresaleQR.png';
import '../styles.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const Presale = () => {
    const [currentStage, setCurrentStage] = useState(null);
    const [paymentCoin, setPaymentCoin] = useState('USDC');
    const [amount, setAmount] = useState('');
    const [tokensToReceive, setTokensToReceive] = useState(0);
    const [usdValue, setUsdValue] = useState(0);
    const [solPrice, setSolPrice] = useState(null); // Store SOL/USD price
    const [purchaseInfo, setPurchaseInfo] = useState(null);
    // Removed unused countdown and countdownText states
    //const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    //const [countdownText, setCountdownText] = useState('');
    const [userPurchased, setUserPurchased] = useState(0);
    const [totalTokensPurchased, setTotalTokensPurchased] = useState(0);
    const [userStaked, setUserStaked] = useState(0);
    const [referralRewards, setReferralRewards] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showGenerateButton, setShowGenerateButton] = useState(true);
    const [showNoPaymentDialog, setShowNoPaymentDialog] = useState(false);
    const [showAddressDialog, setShowAddressDialog] = useState(false);
    const [checkedAddresses, setCheckedAddresses] = useState({ sender: '', receiver: '' });
    const [manualTxHash, setManualTxHash] = useState('');
    const [showManualInput, setShowManualInput] = useState(false);
    const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
    const [agreementChecked, setAgreementChecked] = useState(false);
    const [hasMetInitialPurchase, setHasMetInitialPurchase] = useState(false);

    const navigate = useNavigate();
    const lastFetchTimeRef = useRef(0); // Track last fetch time for debouncing

    const connection = useRef(new Connection(
        process.env.REACT_APP_CHAINSTACK_API_URL || 'https://solana-mainnet.core.chainstack.com/8f1961548a0fd2824b4cbce7bfac5f46',
        'finalized'
    ));

    const PRESALE_WALLET = '4mZ9yFABv79h9UNWR3TDSRqegj2Dzi3yZ74DjdySfusc';
    const USE_MOCK_MODE = false;
    const API_URL = process.env.REACT_APP_API_URL;
    const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
    const USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';

    // Hardcode stage dates and prices
    const stageDates = useMemo(() => [
        { id: 1, start: new Date('2025-03-18T00:00:00Z'), end: new Date('2025-04-16T23:59:59Z'), name: 'Stage 1', usdcPrice: 0.0050 },
        { id: 2, start: new Date('2025-04-17T00:00:00Z'), end: new Date('2025-11-10T23:59:59Z'), name: 'Stage 2', usdcPrice: 0.0100 },
        { id: 3, start: new Date('2025-11-11T00:00:00Z'), end: new Date('2026-12-28T23:59:59Z'), name: 'Stage 3', usdcPrice: 0.0150 },
    ], []);
const [solPriceAvailable, setSolPriceAvailable] = useState(true);

    
const fetchSolPrice = useCallback(async () => {
  try {
    const response = await axios.get(`${API_URL}/api/price/sol`);
    
    if (response.data?.price) {
      setSolPrice(response.data.price);
      setSolPriceAvailable(true);
      return response.data.price;
    }
  } catch (err) {
    console.error('', err);
  }

  // If failed
  setSolPrice(null);
  setSolPriceAvailable(false);
  setError('');
  return null;
}, [API_URL]);
    
    // Fetch SOL price on component mount and periodically
    useEffect(() => {
        fetchSolPrice();
        const interval = setInterval(fetchSolPrice, 300000); // Update every 5 minutes
        return () => clearInterval(interval);
    }, [fetchSolPrice]);

    const pollPurchaseStatus = useCallback(async (purchaseId) => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const response = await axios.get(`${API_URL}/api/user/purchases`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const purchase = response.data.find(p => p.purchase_id === purchaseId && p.transaction_status === 'success');
            if (purchase) {
                const userRes = await axios.get(`${API_URL}/api/user/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const totalTokensPurchased = userRes.data.total_tokens_purchased || 0;

                setPurchaseInfo(null);
                setShowGenerateButton(true);
                setAmount('');
                setTokensToReceive(0);
                setUsdValue(0);
                alert('Payment confirmed successfully!');
                navigate('/dashboard', { state: { fromTransaction: true, totalTokensPurchased } });
            }
        } catch (err) {
            console.error('Error polling purchase status:', err);
        }
    }, [navigate, API_URL]);

    useEffect(() => {
        let interval;

        if (purchaseInfo && !USE_MOCK_MODE) {
            interval = setInterval(() => pollPurchaseStatus(purchaseInfo.purchase_id), 10000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [purchaseInfo, USE_MOCK_MODE, pollPurchaseStatus]);

    const fetchData = useCallback(async () => {
        const now = Date.now();
        // Debounce: Only fetch if at least 5 seconds have passed since the last fetch
        if (now - lastFetchTimeRef.current < 5000) {
            return;
        }
        lastFetchTimeRef.current = now;

        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Please log in to view presale data.');
            navigate('/login');
            return;
        }

        try {
            const [stagesRes, userRes, purchasesRes] = await Promise.all([
                axios.get(`${API_URL}/api/stages`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/api/user/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/api/user/purchases`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);

            const nowDate = new Date();
            const activeStage = stageDates.find((stage) => nowDate >= stage.start && nowDate <= stage.end);
            if (activeStage) {
                const stageData = stagesRes.data.find((s) => s.stage_id === activeStage.id);
                setCurrentStage({ ...stageData, usdcPrice: activeStage.usdcPrice });
            } else {
                setCurrentStage({ ...stagesRes.data[0], usdcPrice: stageDates[0].usdcPrice });
            }

            // Check if the user has made a successful purchase of at least 50 USD
            const hasInitialPurchase = purchasesRes.data.some(purchase =>
                purchase.transaction_status.toLowerCase() === 'success' &&
                parseFloat(purchase.amount) >= 50
            );
            setHasMetInitialPurchase(hasInitialPurchase);

            setUserPurchased(userRes.data.total_tokens_purchased || 0);
            setTotalTokensPurchased(userRes.data.total_tokens_purchased || 0);
            setUserStaked(userRes.data.total_staked || 0);
            setReferralRewards(userRes.data.total_referral_reward_tokens || 0);
        } catch (err) {
            console.error('Fetch data error in Presale:', err.response ? err.response.data : err.message);
            setError('Failed to load presale data. Using defaults.');
            setCurrentStage({
                stage_id: 1,
                stage_name: 'Stage 1',
                tokens_allocated: 500000000,
                tokens_sold: 0,
                price_per_token: 0.001,
                usdcPrice: 0.005,
            });
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, stageDates, API_URL]);

    useEffect(() => {
        fetchData();
    }, [fetchData]); // Initial fetch on mount

    // Separate useEffect for stage checking
    useEffect(() => {
        const stageCheckInterval = setInterval(() => {
            const nowDate = new Date();
            const activeStage = stageDates.find((stage) => nowDate >= stage.start && nowDate <= stage.end);
            if (activeStage && activeStage.id !== currentStage?.stage_id) {
                fetchData(); // Refresh data if the stage has changed
            }
        }, 60000); // Check every minute

        return () => {
            clearInterval(stageCheckInterval);
        };
    }, [currentStage, fetchData, stageDates]);

    // Removed countdown-related useEffect
    /*  useEffect(() => {
        const updateCountdown = () => {
            const now = new Date();
            const activeStage = stageDates.find((stage) => now >= stage.start && now <= stage.end);
            if (activeStage) {
                const timeLeft = activeStage.end - now;
                const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
                setCountdown({ days, hours, minutes, seconds });
                setCountdownText('');
            } else {
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                setCountdownText('Presale Not Active');
            }
        };

        updateCountdown();
        const timer = setInterval(updateCountdown, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [stageDates]);*/

    const handleAmountChange = (e) => {
        const newAmount = e.target.value;
        setAmount(newAmount);
        if (currentStage && newAmount) {
            const parsedAmount = Number(newAmount);
            if (isNaN(parsedAmount) || parsedAmount <= 0) {
                setTokensToReceive(0);
                setUsdValue(0);
                return;
            }

            let usdAmount;
            if (paymentCoin === 'SOL') {
                // For SOL, convert the input amount to USD using the current SOL price
                if (!solPrice) {
                    setError('SOL price not available. Please try again later.');
                    setTokensToReceive(0);
                    setUsdValue(0);
                    return;
                }
                usdAmount = parsedAmount * solPrice;
            } else {
                // For USDC and USDT, the amount is already in USD
                usdAmount = parsedAmount;
            }

            const tokenAmount = usdAmount / currentStage.usdcPrice;
            setTokensToReceive(tokenAmount.toFixed(2));
            setUsdValue(usdAmount.toFixed(2));
        } else {
            setTokensToReceive(0);
            setUsdValue(0);
        }
    };

    const handleGeneratePurchase = useCallback(async (e) => {
        e.preventDefault();
        if (loading || !agreementChecked) return;
        setError('');
        setPurchaseInfo(null);
        setLoading(true);

        const token = localStorage.getItem('token');
        if (!token) {
            setError('Please login first');
            setLoading(false);
            navigate('/login');
            return;
        }
        const parsedAmount = Number(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setError('Please enter a valid amount.');
            setLoading(false);
            return;
        }

        // Determine the minimum amount based on whether the user has met the initial 50 USD purchase
        const minimumAmount = hasMetInitialPurchase ? 10 : 50;
        if (usdValue < minimumAmount) {
            setError(`The minimum amount to buy ABAY is ${minimumAmount} USD.`);
            setLoading(false);
            return;
        }

        try {
            const userRes = await axios.get(`${API_URL}/api/user/dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const walletAddress = userRes.data.wallet_address;
            if (!walletAddress) {
                setError('Please add your wallet address in the Dashboard first.');
                setLoading(false);
                navigate('/dashboard');
                return;
            }

            // For SOL payments, the backend will detect the actual payment coin and update it to SOL if necessary
            const res = await axios.post(
                `${API_URL}/api/user/purchases`,
                {
                    stage_id: currentStage.stage_id,
                    tokens_to_buy: Number(tokensToReceive),
                    payment_coin: paymentCoin, // Initially set to selected coin; may be updated to SOL by backend
                    amount: Number(usdValue), // Use the USD value for consistency
                    wallet_address: walletAddress,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPurchaseInfo({
                purchase_id: res.data.purchase_id,
                amount: Number(usdValue),
                presaleWallet: PRESALE_WALLET,
                userWallet: walletAddress,
                paymentCoin: paymentCoin,
                transaction_status: 'pending',
                stage_id: currentStage.stage_id,
                wallet_address: walletAddress,
                created_at: new Date().toISOString(),
            });
            setShowDialog(true);
        } catch (err) {
            console.error('Purchase error:', err.response ? err.response.data : err.message);
            setError(err.response?.data?.error || 'An unknown error occurred during purchase');
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [loading, currentStage, amount, tokensToReceive, usdValue, paymentCoin, navigate, API_URL, agreementChecked, hasMetInitialPurchase]);

    const handleCopyAddress = useCallback(() => {
        navigator.clipboard.writeText(PRESALE_WALLET).then(() => {
            setShowDialog(false);
            setShowGenerateButton(false);
            setIsConfirmEnabled(false);
            setTimeout(() => setIsConfirmEnabled(true), 30 * 1000); // 30 seconds delay
        }).catch((err) => setError('Failed to copy address: ' + err.message));
    }, []);

    const handleConfirmPayment = useCallback(async (useManualHash = false) => {
        if (!purchaseInfo || loading || (!isConfirmEnabled && !useManualHash)) return;
        setLoading(true);
        setError('');
        setShowNoPaymentDialog(false);
        setShowAddressDialog(false);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Please login first');
                setLoading(false);
                navigate('/login');
                return;
            }

            // Fetch the latest purchase details from the server
            const response = await axios.get(`${API_URL}/api/user/purchases`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const purchase = response.data.find(p => p.purchase_id === purchaseInfo.purchase_id);

            if (!purchase) {
                setError(`Purchase with ID ${purchaseInfo.purchase_id} not found.`);
                setLoading(false);
                return;
            }

            if (purchase.transaction_status === 'success') {
                const userRes = await axios.get(`${API_URL}/api/user/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const totalTokensPurchased = userRes.data.total_tokens_purchased || 0;

                setPurchaseInfo(null);
                setShowGenerateButton(true);
                setAmount('');
                setTokensToReceive(0);
                setUsdValue(0);
                alert('Payment confirmed successfully!');
                navigate('/dashboard', { state: { fromTransaction: true, totalTokensPurchased } });
                return;
            }

            // Helper to check if a transaction is a native SOL transfer
            const isNativeTransfer = (meta) => {
                return (
                    meta.logMessages.some(log => log.includes('Program 11111111111111111111111111111111 invoke')) &&
                    !meta.postTokenBalances.length &&
                    !meta.preTokenBalances.length &&
                    !meta.innerInstructions?.length
                );
            };

            // Helper to check if an address is present in account keys
            const isAddressInAccountKeys = (accountKeys, address) => {
                return accountKeys.some(key => key.pubkey.toBase58() === address.toBase58());
            };

            // Helper to calculate amount paid from top-level instructions
            const calculateAmountFromInstructions = (instructions, selectedMint, senderATA, receiverATA) => {
                let amountPaid = 0;
                for (const instr of instructions) {
                    if (
                        instr.parsed &&
                        (instr.parsed.type === 'transfer' || instr.parsed.type === 'transferChecked') &&
                        instr.programId.toBase58() === 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' &&
                        instr.parsed.info.mint === selectedMint &&
                        instr.parsed.info.source === senderATA.toBase58() &&
                        (instr.parsed.info.destination === PRESALE_WALLET || instr.parsed.info.destination === receiverATA.toBase58())
                    ) {
                        amountPaid = instr.parsed.type === 'transfer'
                            ? parseFloat(instr.parsed.info.amount) / 10 ** 6
                            : parseFloat(instr.parsed.info.tokenAmount.uiAmount);
                        if (amountPaid > 0.002) {
                            break;
                        }
                    }
                }
                return amountPaid;
            };

            // Helper to calculate amount paid from inner instructions
            const calculateAmountFromInnerInstructions = (innerInstructions, selectedMint, senderATA, receiverATA) => {
                let amountPaid = 0;
                const allInstructions = innerInstructions.flatMap(inner => inner.instructions);
                for (const instr of allInstructions) {
                    if (
                        instr.parsed &&
                        instr.parsed.type === 'transfer' &&
                        instr.parsed.info.mint === selectedMint &&
                        instr.parsed.info.source === senderATA.toBase58() &&
                        (instr.parsed.info.destination === PRESALE_WALLET || instr.parsed.info.destination === receiverATA.toBase58())
                    ) {
                        amountPaid = parseFloat(instr.parsed.info.amount) / 10 ** 6;
                        if (amountPaid > 0.002) {
                            break;
                        }
                    }
                }
                return amountPaid;
            };

            // Main transaction checking logic
            const checkTransaction = async (transaction, txHash = null, useManualHashParam = false) => {
                const localToken = localStorage.getItem('token');
                if (!localToken) {
                    setError('Please login first');
                    navigate('/login');
                    return { success: false };
                }

                const senderWallet = transaction.wallet_address;
                if (!senderWallet) {
                    setError(`Sender wallet not found for purchase ${transaction.purchase_id}. Please ensure your wallet address is set in the Dashboard.`);
                    return { success: false };
                }

                const selectedMint = transaction.payment_coin === 'USDC' ? USDC_MINT : USDT_MINT;
                const senderATA = await getAssociatedTokenAddress(new PublicKey(selectedMint), new PublicKey(senderWallet));
                const receiverATA = await getAssociatedTokenAddress(new PublicKey(selectedMint), new PublicKey(PRESALE_WALLET));
                if (!senderATA || !receiverATA) {
                    setError(`Failed to compute ATA for sender or receiver for purchase ${transaction.purchase_id}`);
                    return { success: false };
                }

                let signatures = [];
                let txHashResult = null;
                let amountPaid = 0;
                let actualSender = null;
                let paymentFound = false;
                let actualPaymentCoin = transaction.payment_coin;

                if (useManualHashParam && txHash) {
                    const hashCheck = await axios.get(`${API_URL}/api/payment/check-transaction-hash/${txHash}`, {
                        headers: { Authorization: `Bearer ${localToken}` },
                    });
                    if (hashCheck.data.isUsed) {
                        console.log(`Transaction hash ${txHash} already used for another payment. Skipping.`);
                        setError('This transaction hash has already been used. Please provide a different one.');
                        return { success: false };
                    }
                    signatures = [{ signature: txHash, blockTime: null }];
                } else {
                    console.log(`Fetching signatures for wallet ${senderWallet}`);
                    const signaturesRes = await connection.current.getSignaturesForAddress(new PublicKey(senderWallet), { limit: 20 });
                    signatures = signaturesRes.slice().reverse();
                    console.log(`Found ${signatures.length} signatures for wallet ${senderWallet}`);
                }

                const purchaseCreatedAt = new Date(transaction.created_at).getTime() / 1000;
                let currentSolPrice = solPrice;
                if (!currentSolPrice) {
                    currentSolPrice = await fetchSolPrice();
                }

                for (const sig of signatures) {
                    if (!useManualHashParam) {
                        if (sig.blockTime < purchaseCreatedAt) {
                            console.log(`Skipping transaction ${sig.signature} as it occurred before purchase creation (${sig.blockTime} < ${purchaseCreatedAt})`);
                            continue;
                        }

                        const hashCheck = await axios.get(`${API_URL}/api/payment/check-transaction-hash/${sig.signature}`, {
                            headers: { Authorization: `Bearer ${localToken}` },
                        });
                        if (hashCheck.data.isUsed) {
                            console.log(`Transaction hash ${sig.signature} already used. Skipping.`);
                            continue;
                        }
                    }

                    console.log(`Fetching transaction details for ${sig.signature}`);
                    const transactionData = await connection.current.getParsedTransaction(sig.signature, {
                        commitment: 'finalized',
                        maxSupportedTransactionVersion: 0
                    });
                    if (!transactionData || !transactionData.meta) {
                        console.log(`Transaction ${sig.signature} not found or invalid metadata`);
                        continue;
                    }

                    const { meta, transaction: tx } = transactionData;
                    if (!meta || !tx.message.accountKeys) {
                        console.log(`Transaction ${sig.signature} missing required data`);
                        continue;
                    }

                    const accountKeys = tx.message.accountKeys;
                    const senderIndex = accountKeys.findIndex(key => key.signer);
                    actualSender = senderIndex !== -1 ? accountKeys[senderIndex].pubkey.toBase58() : null;
                    if (actualSender !== senderWallet) {
                        console.log(`Sender mismatch in transaction ${sig.signature}. Expected: ${senderWallet}, Got: ${actualSender}`);
                        continue;
                    }

                    // Check for SOL transfer
                    if (isNativeTransfer(meta)) {
                        console.log(`Detected SOL transfer in transaction ${sig.signature}`);
                        actualPaymentCoin = 'SOL';

                        const senderPreBalance = meta.preBalances[senderIndex] || 0;
                        const senderPostBalance = meta.postBalances[senderIndex] || 0;
                        const receiverIndex = accountKeys.findIndex(key => key.pubkey.toBase58() === PRESALE_WALLET);
                        const receiverPreBalance = receiverIndex !== -1 ? meta.preBalances[receiverIndex] || 0 : 0;
                        const receiverPostBalance = receiverIndex !== -1 ? meta.postBalances[receiverIndex] || 0 : 0;

                        const amountReceivedLamports = receiverIndex !== -1 ? receiverPostBalance - receiverPreBalance : 0;
                        const solAmountPaid = amountReceivedLamports / 1_000_000_000; // Convert lamports to SOL

                        const fee = meta.fee || 0;
                        const senderBalanceChange = (senderPreBalance - senderPostBalance - fee) / 1_000_000_000;
                        if (solAmountPaid <= 0 || senderBalanceChange <= 0) {
                            console.log(`No valid SOL transfer amount found in transaction ${sig.signature}`);
                            continue;
                        }

                        amountPaid = solAmountPaid * currentSolPrice;
                        console.log(`SOL transfer verified: ${amountPaid} USD (${solAmountPaid} SOL at ${currentSolPrice} USD/SOL)`);

                        // Volatility adjustment for SOL payments
                        const expectedAmount = parseFloat(transaction.amount);
                        const percentageDifference = (amountPaid - expectedAmount) / amountPaid; // Positive if user amount is lower, negative if higher
                        if (percentageDifference > 0 && percentageDifference <= 0.25) {
                            console.log(`User amount (${expectedAmount} USD) is ${percentageDifference * 100}% lower than market amount (${amountPaid} USD). Accepting user amount.`);
                            amountPaid = expectedAmount;
                        } else if (percentageDifference < 0) {
                            console.log(`User amount (${expectedAmount} USD) is higher than market amount (${amountPaid} USD). Accepting market amount.`);
                        }
                    } else {
                        // Handle USDC/USDT payments
                        const isSenderMatch = isAddressInAccountKeys(accountKeys, senderATA);
                        const isReceiverMatch = isAddressInAccountKeys(accountKeys, receiverATA);
                        if (!isSenderMatch || !isReceiverMatch) {
                            console.log(`Sender or receiver mismatch in transaction ${sig.signature}. Sender ATA: ${senderATA.toBase58()}, Receiver ATA: ${receiverATA.toBase58()}`);
                            continue;
                        }

                        amountPaid = 0;

                        if (tx.message.instructions) {
                            amountPaid = calculateAmountFromInstructions(tx.message.instructions, selectedMint, senderATA, receiverATA);
                            if (amountPaid > 0) {
                                console.log(`Amount paid from instruction: ${amountPaid} in transaction ${sig.signature}`);
                            }
                        }

                        if (meta.innerInstructions && amountPaid === 0) {
                            amountPaid = calculateAmountFromInnerInstructions(meta.innerInstructions, selectedMint, senderATA, receiverATA);
                            if (amountPaid > 0) {
                                console.log(`Amount paid from inner instructions: ${amountPaid} in transaction ${sig.signature}`);
                            }
                        }

                        if (amountPaid === 0) {
                            console.log(`No valid USDC/USDT transfer found in transaction ${sig.signature}`);
                            continue;
                        }
                    }

                    const expectedAmount = parseFloat(transaction.amount);
                    const lowerBound = expectedAmount * 0.95;
                    const upperBound = expectedAmount * 1.05;
                    paymentFound = amountPaid >= lowerBound && amountPaid <= upperBound;
                    if (paymentFound && !meta.err) {
                        txHashResult = sig.signature;
                        break;
                    }
                }

                if (paymentFound) {
                    // Use the stage price from stageDates based on the purchase's stage_id
                    const stage = stageDates.find(s => s.id === transaction.stage_id);
                    const pricePerToken = stage?.usdcPrice || 0.005;
                    if (pricePerToken <= 0) {
                        setError(`Invalid price per token for stage ${transaction.stage_id}`);
                        return { success: false };
                    }
                    const tokensPurchased = amountPaid / pricePerToken;

                    const updateData = {
                        transaction_status: 'success',
                        transaction_hash: txHashResult,
                        tokens_purchased: tokensPurchased,
                        amount: amountPaid,
                        payment_coin: actualPaymentCoin,
                    };
                    await axios.put(`${API_URL}/api/user/purchases/${transaction.purchase_id}`, updateData, {
                        headers: { Authorization: `Bearer ${localToken}` },
                    });
                    console.log(`Updated purchase ${transaction.purchase_id} to success with amount ${amountPaid} and payment coin ${actualPaymentCoin}`);

                    setCheckedAddresses({
                        sender: actualSender,
                        receiver: actualPaymentCoin === 'SOL' ? PRESALE_WALLET : receiverATA.toBase58(),
                    });
                    setShowAddressDialog(true);
                    await pollPurchaseStatus(transaction.purchase_id);
                    return { success: true };
                } else {
                    setError('No valid payment detected for the purchase. Please enter the transaction hash manually.');
                    return { success: false };
                }
            };

            // Call the checkTransaction function with the fetched purchase data
            const transactionResult = await checkTransaction(purchase, useManualHash ? manualTxHash : null, useManualHash);
            if (!transactionResult.success) {
                setShowManualInput(true);
            }
        } catch (err) {
            console.error('Payment verification error:', err);
            setError('Error verifying payment: ' + (err.response?.data?.message || err.message));
            setCheckedAddresses({ sender: purchaseInfo?.userWallet || '', receiver: PRESALE_WALLET });
            setShowAddressDialog(true);
            setShowManualInput(true);
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [purchaseInfo, loading, manualTxHash, navigate, API_URL, pollPurchaseStatus, isConfirmEnabled, stageDates, solPrice, fetchSolPrice]);

    const handleManualConfirm = useCallback(() => {
        if (!manualTxHash || loading) return;
        handleConfirmPayment(true);
    }, [manualTxHash, loading, handleConfirmPayment]);

    const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const chartData = useMemo(() => ({
        labels: ['Total Purchased', 'Total Staked', 'Total Referral'],
        datasets: [{
            data: [userPurchased || 0, userStaked || 0, referralRewards || 0],
            backgroundColor: ['#06b34e', '#cf8700', '#0643eb'],
            hoverBackgroundColor: ['#00b8bb', '#45b7b0', '#e68a00'],
        }],
    }), [userPurchased, userStaked, referralRewards]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { color: '#333' } },
            tooltip: { callbacks: { label: (context) => `${context.label}: ${formatNumber(context.raw)} ABAY` } },
        },
        animation: { duration: 500 },
    }), []);

    const PieChart = useMemo(() => <Pie data={chartData} options={chartOptions} />, [chartData, chartOptions]);

    const tokensSoldPercentage = currentStage ? (currentStage.tokens_sold / currentStage.tokens_allocated) * 100 : 0;

    return (
        <div className="page-container">
            <Header />
            <section className="content-container container my-4">
                <div className="row">
                    <div className="card p-4 mb-4">
                        <h2 className="text-center mb-4">ABAY Token Presale</h2>
                        {error && <p className="text-danger text-center">{error}</p>}
                    </div>
                    <div className="col-md-3">
                        <div className="card-small p-4 mb-4">
                            {currentStage ? (
                                <>
                                    <p align="left"><strong>Current Stage:</strong></p><p className="text-success">{currentStage.stage_name}</p>
                                </>
                            ) : <p>Loading stage details...</p>}
                        </div>
                        <div className="card-small p-4 mb-4">
                            {currentStage ? (
                                <>
                                    <p align="left"><strong>Stage Price per Token:</strong></p><p className="text-success">${currentStage.usdcPrice} USD</p>
                                </>
                            ) : <p>Loading stage details...</p>}
                        </div>
                        <div className="card-small p-4 mb-4">
                            {currentStage ? (
                                <>
                                    <p align="left"><strong>Time Left:</strong></p>
                                    <p align="left"><strong>Stay tuned: Presale end date will be announced soon.</strong></p>
                                    {/*  
                 {countdownText ? (
                    <p className="text-success">{countdownText}</p>
                  ) : (
                    <div className="countdown-container d-flex">
                      <div className="countdown-unit me-2">
                        <input type="text" value={countdown.days} readOnly className="countdown-input form-control" />
                        <span className="countdown-label">Days</span>
                      </div>
                      <div className="countdown-unit me-2">
                        <input type="text" value={countdown.hours} readOnly className="countdown-input form-control" />
                        <span className="countdown-label">Hours</span>
                      </div>
                      <div className="countdown-unit me-2">
                        <input type="text" value={countdown.minutes} readOnly className="countdown-input form-control" />
                        <span className="countdown-label">Minutes</span>
                      </div>
                      <div className="countdown-unit">
                        <input type="text" value={countdown.seconds} readOnly className="countdown-input form-control" />
                        <span className="countdown-label">Seconds</span>
                      </div>
                    </div>
                  )} 
                    */}

                                </>
                            ) : <p>Loading stage details...</p>}
                        </div>
                    </div>
                    <div className="col-md-5">
                        <div className="card p-4">
                            <h2 align="center">Presale Overview</h2>
                            {currentStage ? (
                                <>
                                    <p align="left"><strong>Total Tokens Sold in Stage 3:</strong></p><p className="text-success">{formatNumber(currentStage.tokens_sold)} / {formatNumber(currentStage.tokens_allocated)}</p>
                                    <div className="progress mt-2">
                                        <div className="progress-bar" role="progressbar" style={{ width: `${tokensSoldPercentage}%` }} aria-valuenow={tokensSoldPercentage} aria-valuemin="0" aria-valuemax="100">
                                            {tokensSoldPercentage.toFixed(2)}%
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <p align="left"><strong>Total Tokens Purchased (All Stages):</strong></p><p className="text-success">{formatNumber(totalTokensPurchased)} ABAY</p>
                                    </div>
                                </>
                            ) : <p>Loading stage details...</p>}
                            <div className="chart-container" style={{ padding: "10px" }}>{PieChart}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card-login-reg p-4 mb-4">
                            <h2 align="center">Buy ABAY</h2>
                            {currentStage ? (
                                <form onSubmit={handleGeneratePurchase}>
                                    <div className="mb-3">
                                        <label htmlFor="paymentCoin" className="form-label" style={{ padding: "10px" }}>Select Payment Coin:</label>
                                        <select 
                                          id="paymentCoin" 
                                          className="form-control" 
                                              value={paymentCoin} 
                                              onChange={(e) => setPaymentCoin(e.target.value)}
                                            >
                                              <option value="USDC">USDC</option>
                                              <option value="USDT">USDT</option>
                                              <option 
                                                value="SOL" 
                                                disabled={!solPriceAvailable}
                                                  >
                                                SOL {!solPriceAvailable ? '(Temporarily Unavailable)' : ''}
                                              </option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="amount" className="form-label" style={{ padding: "10px" }}>Amount ({paymentCoin}):</label>
                                        <input type="number" id="amount" className="form-control" value={amount} onChange={handleAmountChange} placeholder={`Enter amount in ${paymentCoin}`} min="0" step={paymentCoin === 'SOL' ? "0.0001" : "0.01"} required />
                                    </div>
                                    <p style={{ padding: "10px" }}><strong>Tokens to Receive:</strong> {formatNumber(tokensToReceive)} ABAY</p>
                                    <p><strong>USD Value:</strong> ${formatNumber(usdValue)}</p>
                                    <div className="mb-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="agreementCheck"
                                            checked={agreementChecked}
                                            onChange={(e) => setAgreementChecked(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="agreementCheck">
                                            I agree to the{' '}
                                            <a href="/presale-agreement" target="_blank" rel="noopener noreferrer" className="text-primary">
                                                ABAY Token Pre-Sale Agreement
                                            </a>
                                        </label>
                                    </div>
                                    {showGenerateButton ? (
                                        <button type="submit" className="btn btn-primary w-100" disabled={loading || !agreementChecked}>
                                            {loading ? 'Processing...' : 'Pay Now'}
                                        </button>
                                    ) : (
                                        <>
                                            <button type="button" onClick={() => handleConfirmPayment()} className="btn btn-success w-100 mb-2" disabled={loading || !isConfirmEnabled}>
                                                {loading ? 'Verifying...' : 'Confirm Payment'}
                                            </button>
                                            {showManualInput && (
                                                <div>
                                                    <input type="text" className="form-control mb-2" placeholder="Enter Transaction Hash" value={manualTxHash} onChange={(e) => setManualTxHash(e.target.value)} />
                                                    <button type="button" onClick={handleManualConfirm} className="btn btn-primary w-100" disabled={loading}>
                                                        {loading ? 'Verifying...' : 'Confirm with Hash'}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </form>
                            ) : <p>Loading stage data...</p>}
                        </div>
                    </div>
                </div>

                {showDialog && (
                    <div className="dialog-overlay">
                        <div className="dialog-box">
                            <h3>Payment Instructions</h3>
                            <p>Before making your payment, please ensure that cryptocurrency transactions are permitted in your country of residence. If cryptocurrency is legally permitted, copy the provided wallet address and send the payment directly from your registered wallet.</p>
                            <img src={qrImage} alt="QR code for presale wallet" style={{ width: '100%', maxWidth: '200px', margin: '15px 0' }} />
                            <p className="wallet-address">{PRESALE_WALLET}</p>
                            <button onClick={handleCopyAddress} className="btn btn-primary">Copy Address</button>
                        </div>
                    </div>
                )}
                {showNoPaymentDialog && (
                    <div className="dialog-overlay">
                        <div className="dialog-box warning-dialog">
                            <h3>Waiting for Payment</h3>
                            <p>The system is checking for your payment. You can also enter the transaction hash manually below or check the status in Transaction Confirmation.</p>
                            <button onClick={() => setShowNoPaymentDialog(false)} className="btn btn-primary">OK</button>
                        </div>
                    </div>
                )}
                {showAddressDialog && (
                    <div className="dialog-overlay">
                        <div className="dialog-box">
                            <h3>Addresses Checked</h3>
                            <p><strong>Sender:</strong> {checkedAddresses.sender}</p>
                            <p><strong>Receiver:</strong> {checkedAddresses.receiver}</p>
                            <button onClick={() => setShowAddressDialog(false)} className="btn btn-primary">OK</button>
                        </div>
                    </div>
                )}
            </section>
            <Footer />
        </div>
    );
};

export default Presale;
